import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useBoolean
} from '@chakra-ui/react';
import { FaReply } from 'react-icons/fa6';
import { FiExternalLink, FiFile } from 'react-icons/fi';

import { ActivityFile, ActivityParticipantMessage } from 'data/link/schemas';
import { EmailContent } from 'models/link/Task';

import { ReplyEmailSupport } from 'models/user/UserSupport';
import { QuillEditor } from 'common/components';
import { extractAccessToken, formatCompactDateTimeString } from 'common/helpers';
import { TicketListItem } from 'models/link/Link';

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v', 'mkv']);

function getSafeAttachmentUrl(value?: string | null) {
    if (!value) return undefined;

    try {
        const url = new URL(value);
        return url.protocol === 'https:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}

function getFileExtension(file: ActivityFile) {
    const value = file.fileName || file.url;

    try {
        const path = file.fileName || new URL(file.url).pathname;
        return path.split('.').pop()?.toLowerCase() ?? '';
    } catch {
        return value.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() ?? '';
    }
}

function getAttachmentKind(file: ActivityFile) {
    const type = file.type?.trim().toUpperCase();
    const extension = getFileExtension(file);

    if (type === 'IMAGE' || IMAGE_EXTENSIONS.has(extension)) return 'image';
    if (type === 'VIDEO' || VIDEO_EXTENSIONS.has(extension)) return 'video';
    return 'file';
}

function AttachmentLink({ file, url }: { file: ActivityFile; url?: string }) {
    const fileName = file.fileName || 'ไฟล์แนบ';

    if (!url) {
        return (
            <div className="flex min-w-0 items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-500">
                <FiFile className="shrink-0" />
                <span className="truncate" title={fileName}>
                    {fileName}
                </span>
            </div>
        );
    }

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            referrerPolicy="no-referrer"
            className="flex min-w-0 items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-primary-900 hover:border-primary-300 hover:bg-primary-50"
        >
            <FiFile className="shrink-0" />
            <span className="truncate" title={fileName}>
                {fileName}
            </span>
            <FiExternalLink className="ml-auto shrink-0" />
        </a>
    );
}

function RemarkAttachment({ file }: { file: ActivityFile }) {
    const url = getSafeAttachmentUrl(file.url);
    const kind = getAttachmentKind(file);
    const [hasMediaError, setHasMediaError] = useState(false);
    const fileName = file.fileName || 'ไฟล์แนบ';

    if (!url || kind === 'file' || hasMediaError) {
        return <AttachmentLink file={file} url={url} />;
    }

    if (kind === 'image') {
        return (
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                className="block overflow-hidden rounded-md border border-neutral-200 bg-neutral-50"
            >
                <img
                    src={url}
                    alt={fileName}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setHasMediaError(true)}
                    className="max-h-64 w-full object-contain"
                />
                <p className="truncate border-t border-neutral-200 px-3 py-2 text-xs text-neutral-600">
                    {fileName}
                </p>
            </a>
        );
    }

    return (
        <div className="overflow-hidden rounded-md border border-neutral-200 bg-neutral-50">
            <video
                controls
                preload="metadata"
                onError={() => setHasMediaError(true)}
                className="max-h-72 w-full bg-black"
            >
                <source src={url} />
            </video>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                className="flex min-w-0 items-center gap-2 border-t border-neutral-200 px-3 py-2 text-xs text-primary-900 hover:bg-primary-50"
            >
                <span className="truncate" title={fileName}>
                    {fileName}
                </span>
                <FiExternalLink className="ml-auto shrink-0" />
            </a>
        </div>
    );
}

type Props = {
    noReplyButton?: boolean;
    task: TicketListItem;
    activity: ActivityParticipantMessage;
    isOwnMessage?: boolean;
    onReplyEmail: (reply: ReplyEmailSupport) => void;
    onSelectCommentReply?: (activity: ActivityParticipantMessage) => void;
};

function ParticipantMessage(props: Props) {
    const {
        noReplyButton,
        task,
        activity,
        isOwnMessage,
        onReplyEmail,
        onSelectCommentReply
    } = props;

    const [showReplyModal, setShowReplyModal] = useBoolean();
    const messageType = activity.messageType?.trim().toUpperCase() ?? '';
    const isEmailMessage = messageType.includes('EMAIL');
    const isRemarkMessage = messageType === 'REMARK';

    const email = useMemo(() => {
        if (!isEmailMessage || !activity.data) {
            return {} as EmailContent;
        }

        try {
            return JSON.parse(activity.data) as EmailContent;
        } catch {
            return {} as EmailContent;
        }
    }, [activity.data, isEmailMessage]);
    const messageContent = isEmailMessage
        ? email.content_body
        : activity.messageText ||
          activity.remarkEventDetail ||
          activity.eventMessage ||
          activity.data;
    const safeMessageContent = useMemo(
        () => DOMPurify.sanitize(messageContent || '-'),
        [messageContent]
    );
    const quotedEmailContent = useMemo(
        () =>
            DOMPurify.sanitize(
                '<p><br /></p>' +
                    '<p>______________________________</p>' +
                    '<p><b>From: </b>' +
                    (email.email_from ?? '') +
                    '</p>' +
                    '<p><b>Subject: </b>' +
                    (email.subject ?? '') +
                    '</p>' +
                    '<p><br /></p>' +
                    safeMessageContent
            ),
        [email.email_from, email.subject, safeMessageContent]
    );

    const emailFrom = useRef(extractAccessToken().email).current;
    const [emailContent, setEmailContent] = useState(() => quotedEmailContent);

    const handleClickReply = useCallback(async () => {
        onReplyEmail({
            account: {
                sid: email.system_id || '',
                companycode: '1000',
                email: emailFrom
            },
            reply_data: {
                // content_body: emailContent.replace(/<[^>]+>/g, '\n'), // Replace all html tags with new line
                content_body: emailContent,
                email_from: emailFrom,
                mail_to_list: [email.email_from],
                // mail_cc_list: Array.from(new Set([...email.mail_cc_list, emailCC])),
                mail_cc_list: [''],
                object_link: email.object_link,
                ref_email_code: email.ref_email_code,
                subject: email.subject,
                system_id: email.system_id,
                type: ''
            }
        });
    }, [emailFrom, emailContent, email, onReplyEmail]);

    const canReply =
        !noReplyButton &&
        isEmailMessage &&
        Boolean(email.email_from && email.object_link && email.ref_email_code);
    const canReplyComment =
        !noReplyButton && isRemarkMessage && Boolean(onSelectCommentReply);
    const createdOn = formatCompactDateTimeString(activity.createdOn);
    const messageClassName = isRemarkMessage
        ? `w-fit max-w-[78%] rounded-2xl border px-4 ${
              isOwnMessage
                  ? 'rounded-tr-md border-primary-200 bg-primary-50'
                  : 'rounded-tl-md border-neutral-200 bg-white'
          }`
        : noReplyButton
        ? 'w-full rounded-lg border border-l-4 border-neutral-200 border-l-primary-900 bg-neutral-50 px-3'
        : 'w-full rounded-lg border border-primary-900 bg-primary-50 px-3';

    return (
        <React.Fragment>
            <div
                className={
                    isRemarkMessage
                        ? `flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`
                        : 'flex'
                }
            >
                <div className={messageClassName}>
                    <div className="py-2">
                        {(activity.creatorFullname || createdOn || messageType) && (
                            <div className="mb-2 flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
                                {activity.creatorFullname && (
                                    <span className="font-medium text-neutral-700">
                                        {activity.creatorFullname}
                                    </span>
                                )}
                                {messageType && <span>{messageType}</span>}
                                {createdOn && <span>{createdOn}</span>}
                            </div>
                        )}
                        <div
                            className="break-words whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{ __html: safeMessageContent }}
                        />

                        {canReply && (
                            <Button
                                size="sm"
                                onClick={setShowReplyModal.on}
                                className="ml-auto flex items-center gap-2 bg-primary-900 text-white"
                            >
                                <FaReply />
                                <span>Reply</span>
                            </Button>
                        )}

                        {canReplyComment && (
                            <button
                                type="button"
                                onClick={() => onSelectCommentReply?.(activity)}
                                className="ml-auto mt-2 flex items-center gap-1 text-xs font-medium text-primary-900 hover:underline"
                            >
                                <FaReply />
                                <span>ตอบกลับ</span>
                            </button>
                        )}
                    </div>

                    {activity.files && activity.files.length > 0 && (
                        <div className="border-t border-zinc-300 py-2">
                            <p className="mb-2 text-xs font-medium text-neutral-500">ไฟล์แนบ</p>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {activity.files.map((file, index) => (
                                    <RemarkAttachment
                                        key={`${file.key || index}-${file.url}`}
                                        file={file}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal
                size="6xl"
                isOpen={showReplyModal}
                onClose={setShowReplyModal.off}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        Reply <span className="text-primary-900">{task.ticketNO}</span>
                    </ModalHeader>
                    <ModalBody className="space-y-4">
                        {/* <div>
                            <Label>E-mail from</Label>
                            <Input
                                disabled
                                placeholder="E-mail from"
                                value={emailFrom}
                            />
                        </div>

                        <div>
                            <Label>E-mail to</Label>
                            <Input
                                disabled
                                placeholder="E-mail to"
                                value={email.email_from}
                            />
                        </div> */}

                        {/* <div>
                            <Label>E-mail CC</Label>
                            <Input
                                placeholder="E-mail CC"
                                value={emailCC}
                                onChange={event => setEmailCC(event.target.value)}
                            />
                        </div> */}

                        <QuillEditor
                            value={emailContent}
                            onChange={setEmailContent}
                            editorClassName="[&>.ql-container]:min-h-[250px]"
                        />
                    </ModalBody>
                    <ModalFooter className="gap-2">
                        <Button
                            size="sm"
                            className="bg-primary-900 leading-none text-white"
                            onClick={setShowReplyModal.off}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleClickReply}
                            className="border-primary-900 leading-none text-primary-900"
                        >
                            Send
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </React.Fragment>
    );
}

export default memo(ParticipantMessage);
